import PropTypes from 'prop-types'
import Router from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import compose from 'recompose/compose'
import Dialog from 'components/common/dialog'
import modalDuck from 'components/modal/duck'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import BackdropModal from 'components/portal/backdrop-modal'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { withI18next } from 'lib/i18n/withI18next'
import cartDuck from '../duck'
import cartItemsDuck from '../cart-items/duck'
import { CART_FREE_GIFT_MODAL_ID } from './const'
import freeGiftDuck from './duck'
import FreeGiftRow from './free-gift-row'
import {
  getAddToCartCartParamForAvailableAttribute,
  handleAddToCart,
  isFreeGiftUnlocked,
} from './utils'
import styles from './style.scss'

const FreeGift = ({
  cartData,
  cartInfo,
  closeModal,
  getFreeGift,
  freeGiftProduct,
  handleCart,
  internationalization: { country, language, locale },
  invalidateFreeGift,
  resetCart,
  t,
  updateCart,
  updateIsFreeGiftAdded,
}) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [pendingProductId, setPendingProductId] = useState(null)
  // setPendingProductId's update isn't visible until the next render, so a
  // second click that lands before that render can't see it -- pending
  // itself is only for the UI (disabling/labeling the button); this ref is
  // the actual synchronous re-entrancy guard against a double add-to-cart.
  const pending = useRef(false)

  // componentDidMount
  useEffect(() => {
    if (!cartInfo) {
      return
    }

    const optionFreeGift = {
      type: 'GET',
      id_cart: cartInfo.metadata.id_cart,
      id_lang: locale,
    }

    invalidateFreeGift()
    getFreeGift(optionFreeGift)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The picker renders inline in the cart drawer instead of its own
  // backdrop -- freeGiftOpen is what the drawer checks to swap the gift
  // list back out for the normal cart body, so it must be cleared here
  // too, not just the modal registry entry.
  const closeFreeGiftView = () => {
    closeModal(CART_FREE_GIFT_MODAL_ID)
    updateCart({
      cartOpen: true,
      freeGiftOpen: false,
      couponUsed: cartData.couponUsed,
      isStoreCreditUsed: cartData.isStoreCreditUsed,
    })
  }

  // The back arrow just returns to the cart body -- picking a gift or
  // declining one are the two paths that continue on to checkout, since
  // reaching this picker in the first place only happens by way of
  // checkout gating on an undecided free gift (see Cart's goToCheckout).
  const returnToCart = () => {
    closeFreeGiftView()
  }

  const continueToCheckout = () => {
    closeFreeGiftView()
    Router.push('/checkout', `/${country}/${language}/checkout`)
  }

  const closeErrorModal = () => {
    setShowErrorDialog(false)
  }

  const hasError = (error) => {
    const errorMsg =
      error?.message ||
      (typeof error === 'string' && error) ||
      hasLocizeTranslation(
        t,
        'FREE_GIFT_ERROR_MESSAGE_KEY',
        'Oops something went wrong on our side.',
      )
    setErrorMessage(errorMsg)
    setShowErrorDialog(true)
  }

  const addGiftToBag = async (product) => {
    if (pending.current) return
    pending.current = true
    setPendingProductId(product.id_product)
    const cartParam = getAddToCartCartParamForAvailableAttribute(
      cartData,
      product,
    )
    try {
      await handleAddToCart(
        resetCart,
        handleCart,
        cartData,
        cartParam,
        continueToCheckout,
        hasError,
        updateCart,
        updateIsFreeGiftAdded,
      )
    } finally {
      pending.current = false
      setPendingProductId(null)
    }
  }

  return (
    <div className="free-gift-container">
      <style jsx>{styles}</style>
      <div className="free-gift-header">
        <Icon
          className="free-gift-back"
          onClick={returnToCart}
          src={ICONS.arrowHeadLeft}
          size={ICON_SIZE.medium}
        />
        <span className="free-gift-header__title">{t('Free Gift Alert!')}</span>
      </div>
      <div className="free-gift-intro">
        <span className="free-gift-intro__title">
          {t("You're eligible for one of the free gifts below.")}
        </span>
        <span className="free-gift-intro__subtitle">
          {t("Claim now before it's gone!")}
        </span>
      </div>
      {!!freeGiftProduct && (
        <div className="free-gift-list">
          {freeGiftProduct.free_gifts
            .filter((product) => !product.stock?.is_sold_out)
            .map((product, index) => (
              // The API can return multiple entries for the same
              // id_product (distinct voucher instances of one gift), so
              // the index has to be part of the key too.
              <FreeGiftRow
                key={`${product.id_product}-${index}`}
                product={product}
                isEligible={isFreeGiftUnlocked(product)}
                isAdding={pendingProductId === product.id_product}
                isDisabled={!!pendingProductId}
                onAddToBag={addGiftToBag}
              />
            ))}
        </div>
      )}
      <div className="free-gift-footer">
        <Button
          type={BUTTON_TYPES.outlinedLight}
          className="free-gift-footer__decline"
          onClick={continueToCheckout}
        >
          {t('I do not want a free gift')}
          <span aria-hidden="true">&nbsp;→</span>
        </Button>
        <span className="free-gift-footer__note">
          {t("You're missing out on something free!")}
        </span>
      </div>
      <BackdropModal
        freezeBody
        isOpen={showErrorDialog}
        onBackdropClick={closeErrorModal}
        // Override Freegift modal
        zIndex={100005}
      >
        <Dialog
          title={errorMessage}
          confirmText={t('OK')}
          onConfirm={closeErrorModal}
          confirmable
        />
      </BackdropModal>
    </div>
  )
}

FreeGift.defaultProps = {
  freeGiftProduct: null,
}

FreeGift.propTypes = {
  cartData: PropTypes.shape({
    couponUsed: PropTypes.bool,
    isStoreCreditUsed: PropTypes.bool,
  }).isRequired,
  cartInfo: PropTypes.shape({
    metadata: PropTypes.shape({
      id_cart: PropTypes.number,
    }),
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  getFreeGift: PropTypes.func.isRequired,
  freeGiftProduct: PropTypes.shape({
    free_gifts: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  handleCart: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
    language: PropTypes.string,
    locale: PropTypes.number,
  }).isRequired,
  invalidateFreeGift: PropTypes.func.isRequired,
  resetCart: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  updateCart: PropTypes.func.isRequired,
  updateIsFreeGiftAdded: PropTypes.func.isRequired,
}

export default compose(
  connect(
    (state) => ({
      cartData: state.cartData,
      cartInfo: state.cart.payload,
      freeGiftProduct: state.freeGift.payload,
      internationalization: state.internationalization,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          closeModal: modalDuck.creators.hide,
          getFreeGift: freeGiftDuck.creators.get,
          handleCart: cartDuck.creators.get,
          invalidateFreeGift: freeGiftDuck.creators.invalidate,
          resetCart: cartDuck.creators.invalidate,
          updateCart: cartItemsDuck.creators.update,
          updateIsFreeGiftAdded: cartItemsDuck.creators.updateIsFreeGiftAdded,
        },
        dispatch,
      ),
  ),
  withI18next(),
)(FreeGift)
